
import DeleteFtpUserDialog from '@/components/dialogs/ftp-users/DeleteFtpUserDialog.vue';
import EditFtpUserDialog from '@/components/dialogs/ftp-users/EditFtpUserDialog.vue';
import { FtpUser, FtpUserStatus, FtpUserUpdateDTO } from '@/services/FtpUser/interfaces';
import { FtpUserRepositoryInterface, FtpUserSearchResult, FtpUserWithLogs } from '@/services/FtpUser/interfaces/index';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import { AxiosError } from 'axios';
import generateConnectUrl from './utils';


@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'ftpUserRepository',
    'serverRepository',
  ],
  components: {
    EditFtpUserDialog,
    DeleteFtpUserDialog,
  },
})
export default class AllFtpUsers extends Vue {
  public loading: boolean = true;

  public ftpUserRepository: FtpUserRepositoryInterface;
  public serverRepository: ServerRepositoryInterface;

  public search: string = '';
  public searchResult: FtpUserSearchResult = {
    ftpUsers: [],
    total: 0,
    limit: 0,
    offset: 0,
    prev: null,
    next: null,
  }

  public serversMap: { [key: string]: ServerInterface } = {};

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Username', value: 'username' },
    { text: 'Account User', value: 'accountName' },
    { text: 'Server', value: 'server' },
    { text: 'Directory', value: 'homeDir' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions' },
  ];

  public getStatus(ftpUser: FtpUser): string {
    return FtpUserStatus[ftpUser.status];
  }

  public editFtpUserDialogData: {
    ftpUser: FtpUserWithLogs,
    server: ServerInterface,
  } | false = false;
  public editFtpUserDialog: string = '';

  public deleteFtpUserDialog: string = '';
  public ftpUserForDeleteDialog: FtpUser | false = false;

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async changePage(): Promise<void> {
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;
      await this.getFtpUsers();
      await this.getServers();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getFtpUsers(): Promise<void> {
    this.searchResult = await this.ftpUserRepository.searchFtpUsers(
      this.options.page,
      this.search,
    );
  }

  public async getServers(): Promise<void> {
    const ids = this.searchResult.ftpUsers.map((ftpUser) => ftpUser.server);
    if (!ids.length) {
      return;
    }

    const servers = await this.serverRepository.getServersByIds(ids);
    for (const server of servers) {
      this.serversMap[server._id] = server;
    }
  }

  public createFtpUser(): void {
    this.$router.push({ name: 'ftp_user_create' });
  }

  public async editFtpUser(ftpUser: FtpUser): Promise<void> {
    try {
      const updateData: FtpUserUpdateDTO = {
        password: ftpUser.password,
      }

      await this.ftpUserRepository.updateFtpUser(ftpUser.id, updateData);

      this.$successDialog.show('FTP User update started!');
    } catch (err) {
      this.handleError(err);
    }
  }

  public async deleteFtpUser(ftpUser: FtpUser): Promise<void> {
    try {
      await this.ftpUserRepository.deleteFtpUser(ftpUser.id);

      this.$successDialog.show('FTP User deletion started!');
    } catch (err) {
      this.handleError(err);
    }
  }

  public async redirectToConnectPage(id: number): Promise<void> {
    try {
      const ftpUser = await this.ftpUserRepository.findOneById(id);
      if(!ftpUser) {
        return;
      }

      const hostname: string | undefined = this.serversMap[ftpUser.server]?.hostname;
      if(!hostname) {
        this.$errorDialog.show('Unknown server!');
        return;
      }

      const jwtToken = this.$store.getters.jwt.replace('Bearer ', '');

      document.cookie = `jwt=${jwtToken}; path=/; SameSite=Lax;`;
      document.cookie = `ftpUserId=${ftpUser.id}; path=/; SameSite=Lax;`;
      document.cookie = `websiteId=${ftpUser.websiteId}; path=/; SameSite=Lax;`;
      document.cookie = `username=${ftpUser.username}; path=/; SameSite=Lax;`;

      window.open(generateConnectUrl(), '_blank');
      
    } catch (err) {
      this.handleError(err);
    }
  }

  public async showEditDialog(id: number): Promise<void> {
    try {
      const ftpUser = await this.ftpUserRepository.findOneById(id);
      if(!ftpUser) {
        return;
      }

      this.editFtpUserDialogData = {
        ftpUser,
        server: this.serversMap[ftpUser.server],
      }

      this.editFtpUserDialog = 'edit-ftp-user-dialog';
    } catch (err) {
      this.handleError(err);
    }
  }

  public showDeleteDialog(id: number): void {
    const ftpUser = this.searchResult.ftpUsers.find((ftpUser) => ftpUser.id === id);
    if (!ftpUser) {
      return;
    }

    this.ftpUserForDeleteDialog = ftpUser;
    this.deleteFtpUserDialog = 'delete-ftp-user-dialog';
  }

  private handleError(err: unknown) {
    const axiosErr = err as AxiosError;
    if (axiosErr.isAxiosError) this.$errorDialog.show(axiosErr.response?.data?.message);
    else if (err instanceof Error) this.$errorDialog.show(err.message);
  }
}
